// features/settings/dashboard/dashStyles.ts — the styled pieces of the dashboard card
// kit (1.27.70), kept apart from DashCard.tsx's components so both files stay
// fast-refresh friendly. Same owner style rules: no capitals, no bright outlines,
// hover tints the background and never the border.
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { softControlMotion } from '@/theme/interactions';

export type CardTone = 'default' | 'accent' | 'danger';

/**
 * The query container around the grid. A container query can never measure the
 * element that declares the container, so the grid needs this wrapper to reflow
 * by its own available width (sidebar open, collapsed or hidden), not the viewport.
 */
export const DashScope = styled.div`
  container-type: inline-size;
  min-width: 0;
`;

/**
 * The grid, inside a DashScope: six columns when wide, two in the middle, one on
 * a phone. Cards set their own spans for each of the three layouts.
 */
export const DashGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 16px;
  align-items: stretch;
  @container (max-width: 1059.98px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @container (max-width: 619.98px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const IconTile = styled.span<{ $tone?: CardTone }>`
  flex: none;
  display: inline-grid;
  place-items: center;
  width: 40px;
  height: 40px;
  border-radius: ${({ theme }) => theme.radius.md};
  color: ${({ theme, $tone }) =>
    $tone === 'danger' ? theme.color.danger : theme.color.accentCyan};
  background: ${({ theme, $tone }) =>
    `color-mix(in srgb, ${$tone === 'danger' ? theme.color.danger : theme.color.accentCyan} 14%, transparent)`};
`;

export const RowLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  line-height: ${({ theme }) => theme.lineHeight.sm};
  font-weight: 500;
  color: ${({ theme }) => theme.color.textPrimary};
  overflow-wrap: anywhere;
`;

export const RowHint = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs};
  line-height: ${({ theme }) => theme.lineHeight.xs};
  color: ${({ theme }) => theme.color.textMuted};
  overflow-wrap: anywhere;
`;

export const RowList = styled.div`
  display: flex;
  flex-direction: column;
`;

/**
 * Navigation that looks like a Button ("Edit profile", "Upgrade to Premium",
 * "Manage"). A real <a>, so it opens in a new tab and reads as a link, and the
 * same fills as ui/Button: primary is the one accent fill, secondary a surface.
 */
export const LinkButton = styled(Link)<{ $variant?: 'primary' | 'secondary'; $size?: 'sm' | 'md' }>`
  ${softControlMotion}
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-family: ${({ theme }) => theme.font.body};
  font-weight: 600;
  font-size: ${({ theme, $size }) => ($size === 'sm' ? theme.fontSize.xs : theme.fontSize.sm)};
  padding: ${({ $size }) => ($size === 'sm' ? '6px 12px' : '9px 16px')};
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid
    ${({ theme, $variant }) => ($variant === 'primary' ? 'transparent' : theme.color.hairSoft)};
  background: ${({ theme, $variant }) =>
    $variant === 'primary' ? theme.color.accentCyan : theme.color.surface};
  color: ${({ theme, $variant }) =>
    $variant === 'primary' ? theme.color.onAccent : theme.color.textPrimary};
  text-decoration: none;
  white-space: nowrap;
  &:hover {
    background-color: ${({ theme, $variant }) =>
      $variant === 'primary'
        ? `color-mix(in srgb, ${theme.color.accentCyan} 92%, ${theme.color.textPrimary})`
        : `color-mix(in srgb, ${theme.color.surface} 94%, ${theme.color.textPrimary})`};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;
